import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type Enrollment = {
  _id: string
  user: {
    _id: string
    firstName: string
    lastName: string
    email: string
    profilePicture: string
    gender: string
    status: string
    phoneNumber: string
    religion: string
    bloodGroup: string
    emergencyContactRelation?: string
  }
  admission: {
    firstName?: string
    lastName?: string
    fatherName: string
    motherName?: string
    gender: string
    houseNo: string
    buildingName?: string
    blockNo?: string
    city: string
    area: string
    allergies: string
    medications: string
    dateOfBirth: string
    bloodGroup: string
    religion: string
    religionType?: string | null
    religionTypeOther?: string | null
    motherTongue: string
    noOfSiblings: number
    noOfSiblingsEnrolled?: number | null
    fatherPhoneNumber?: string
    fatherOccupation?: string
    fatherCompanyName?: string
    fatherSalary?: number
    motherPhoneNumber?: string
    motherOccupation?: string
    motherCompanyName?: string
    motherSalary?: number
    fatherEmail?: string
    motherEmail?: string
    fatherCnic?: string
    motherCnic?: string
    guardianName?: string
    guardianRelation?: string
    guardianPhoneNumber?: string
    guardianCnic?: string
    guardianEmail?: string
    guardianOccupation?: string
    guardianCompanyName?: string
    guardianSalary?: number
    fatherPicture?: string
    motherPicture?: string
    guardianPicture?: string
    emergencyContactRelation?: string
    admissionDate?: string | null
    nationality?: string
    placeOfBirth?: string
    bFormNo?: string | null
    email?: string
    mobileNO?: string
    height?: string
    weight?: string
    languageKnown?: string[]
    hasMedicalIssue?: boolean
    hasSiblings?: boolean
    isVaccinated?: boolean
    isFatherVaccinated?: boolean
    isMotherVaccinated?: boolean
    isGuardianVaccinated?: boolean
    temporaryGR?: string
    prevSchoolName?: string
    prevSchoolAddress?: string
    prevClass?: string
    lastResult?: string
    sameAsCurrentAddress?: boolean
    permanentCity?: string | null
    permanentArea?: string | null
    permanentBuildingName?: string | null
    permanentBlockNo?: string | null
    permanentHouseNo?: string | null
  }
  parentUsers: {
    firstName: string
    phoneNumber: string
    email: string
  }[]
  father?: {
    user: {
      firstName: string
      lastName: string
      phoneNumber: string
      email: string
    }
    cnic?: string | null
    relation: string
  }
  mother?: {
    user: {
      firstName: string
      lastName: string
      phoneNumber: string
      email: string
    }
    cnic?: string | null
    relation: string
  }
  guardian?: {
    user: {
      firstName: string
      lastName: string
      phoneNumber: string
      email: string
    }
    cnic?: string | null
    relation: string
  }
  discounts: {
    discount: string
    discountType: string
  }[]
  student: {
    _id: string
    grNumber: string
    fatherName?: string
    dateOfBirth: string
    motherTongue: string
    createdAt: string
    profilePicture?: string
    hasMedicalIssue?: boolean
    height?: string
    weight?: string
    allergies?: string
    medications?: string
  }
  class: {
    _id: string
    name: string
  }
  section: {
    _id: string
    name: string
  }
  system: {
    _id: string
    name: string
  }
  group?: {
    _id: string
    name: string
  } | null
  branch: {
    _id: string
    name: string
  }
  session: {
    _id: string
    name: string
    title?: string
    startDate?: string
    endDate?: string
  }
  status: string
  gender?: string
  createdAt: string
  profilePicture?: string
  // Promotion trail: which session (if any) this enrollment was promoted
  // from/to, for the "Promoted to/from <session>" badge.
  promotedFromSession?: { _id: string; title?: string } | null
  promotedToSession?: { _id: string; title?: string } | null
}

export type PaginationData = {
  page: number
  hasNextPage?: boolean
  hasPrevPage?: boolean
  limit: number
  total: number
  totalPages: number
}

export type ApiResponse = {
  success: boolean
  data: Enrollment[]
  pagination: PaginationData
}

export type SingleEnrollmentResponse = {
  success: boolean
  data: Enrollment | null
  message?: string
}

export type UpdateProfilePictureResponse = {
  success: boolean
  message: string
  data: {
    profilePicture: string
    student: {
      id: string
      grNumber: string
      name: string
    }
    user?: {
      id: string
      firstName: string
      lastName: string
    }
  }
}

export type RemoveProfilePictureResponse = {
  success: boolean
  message: string
  data: {
    student: {
      id: string
      grNumber: string
      profilePicture: string | null
    }
    user?: {
      id: string
      firstName: string
      lastName: string
      profilePicture: string | null
    }
  }
}

const ENROLLMENT_ENDPOINT = {
  ENROLLMENTS: "/enrollment",
}

const getEnrollments = async ({
  page = 1,
  limit = 10,
  sortBy,
  sortOrder,
  searchText,
  system,
  branch,
  session,
  class: classId,
  section,
  group,
  gender,
  student: studentId,
  admissionStatus,
  statusScope,
}: {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
  searchText?: string
  system?: string
  branch?: string
  session?: string
  class?: string
  section?: string
  group?: string
  gender?: string
  student?: string
  // One SPECIFIC admission status (e.g. "struck_off") — from a filter
  // dropdown, and wins outright over statusScope below.
  admissionStatus?: string
  // What this screen means by "the students I show" when no specific status
  // is picked. "fees" = active + struck-off + left-without-info (a
  // left-without-info student must stay reachable to pay). "onroll" = active
  // + struck-off only — everyone else is gone from lists/RMS/head count/
  // receivables alike. Omit to keep the old, unfiltered-by-status behaviour.
  statusScope?: "fees" | "onroll"
} = {}): Promise<ApiResponse> => {
  const response = await apiClient({
    url: appendQueryParams(ENROLLMENT_ENDPOINT.ENROLLMENTS, {
      page,
      limit,
      sortBy,
      sortOrder,
      searchText,
      system,
      branch,
      session,
      class: classId,
      section,
      group,
      gender,
      student: studentId,
      admissionStatus,
      statusScope,
    }),
    method: "GET",
  })
  return response.data
}

const getEnrollmentsByClass = async (
  classId: string,
  page: number = 1,
  limit: number = 100
): Promise<ApiResponse> => {
  const response = await apiClient({
    url: appendQueryParams(ENROLLMENT_ENDPOINT.ENROLLMENTS, {
      page,
      limit,
      class: classId,
      status: "active",
    }),
    method: "GET",
  })
  return response.data
}

const getEnrollmentById = async (enrollmentId: string): Promise<Enrollment> => {
  const response = await apiClient({
    url: `${ENROLLMENT_ENDPOINT.ENROLLMENTS}/${enrollmentId}`,
    method: "GET",
  })

  if (!response.data.success) {
    throw new Error("Failed to fetch enrollment")
  }

  return response.data.data
}

const updateEnrollmentProfile = async (
  enrollmentId: string,
  data: Record<string, any>
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient({
    url: `${ENROLLMENT_ENDPOINT.ENROLLMENTS}/${enrollmentId}/profile`,
    method: "PATCH",
    data,
  })
  return response.data
}

/** Get enrollment by student id (e.g. for transport fee modal). Returns first active enrollment for the student. */
const getEnrollmentByStudentId = async (
  studentId: string,
  branchId?: string
): Promise<Enrollment | null> => {
  const res = await getEnrollments({
    student: studentId,
    branch: branchId,
    limit: 1,
    page: 1,
  })
  const list = res?.data ?? []
  return list.length > 0 ? list[0] : null
}

const getUserEnrollment = async (
  userId: string,
  sessionId: string
): Promise<SingleEnrollmentResponse> => {
  try {
    const response = await apiClient({
      url: `${ENROLLMENT_ENDPOINT.ENROLLMENTS}/user/${userId}?sessionId=${sessionId}`,
      method: "GET",
    })

    return {
      success: response.data.success,
      data: response.data.data || null,
      message: response.data.message,
    }
  } catch (error: any) {

    return {
      success: false,
      data: null,
      message: error.message || "Failed to fetch enrollment",
    }
  }
}

const updateStudentProfilePicture = async (
  enrollmentId: string,
  imageFile: File
): Promise<UpdateProfilePictureResponse> => {
  try {
    
    const formData = new FormData()
    formData.append("profilePicture", imageFile)

    const response = await apiClient({
      url: `${ENROLLMENT_ENDPOINT.ENROLLMENTS}/${enrollmentId}/profile-picture`,
      method: "PATCH",
      data: formData,
    })

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data,
    }
  } catch (error: any) {
    console.error("Error updating profile picture:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    })
    
    throw new Error(
      error.response?.data?.message || "Failed to update profile picture"
    )
  }
}

/**
 * Remove student profile picture
 */
const removeStudentProfilePicture = async (
  enrollmentId: string
): Promise<RemoveProfilePictureResponse> => {
  try {
    const response = await apiClient({
      url: `${ENROLLMENT_ENDPOINT.ENROLLMENTS}/${enrollmentId}/profile-picture`,
      method: "DELETE",
    })

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data,
    }
  } catch (error: any) {
    console.error("Error removing profile picture:", error.message)
    throw new Error(
      error.response?.data?.message || "Failed to remove profile picture"
    )
  }
}

const uploadProfilePictureFromCamera = async (
  enrollmentId: string,
  imageSrc: string
): Promise<UpdateProfilePictureResponse> => {
  try {
    
    const base64Data = imageSrc.includes('base64,') 
      ? imageSrc.split(',')[1] 
      : imageSrc
    
    const binaryString = atob(base64Data)
    const bytes = new Uint8Array(binaryString.length)
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    
    const blob = new Blob([bytes], { type: 'image/jpeg' })
    const file = new File([blob], `camera_${Date.now()}.jpg`, {
      type: 'image/jpeg'
    })
    
    
    return await updateStudentProfilePicture(enrollmentId, file)
    
  } catch (error: any) {
    console.error("Error uploading from camera:", error)
    throw new Error("Failed to upload picture from camera. Please try uploading a file instead.")
  }
}

export {
  getEnrollments,
  getEnrollmentsByClass,
  getEnrollmentById,
  updateEnrollmentProfile,
  getEnrollmentByStudentId,
  getUserEnrollment,
  updateStudentProfilePicture,
  removeStudentProfilePicture,
  uploadProfilePictureFromCamera,
}
